import { useState, useEffect, useCallback, useRef } from 'react';
import { QueryDocumentSnapshot, DocumentData } from 'firebase/firestore';
import {
  IconPlus,
  IconPencil,
  IconTrash,
  IconUpload,
  IconChevronLeft,
  IconChevronRight,
} from '@tabler/icons-react';
import {
  listForestryOperations,
  createForestryOperation,
  updateForestryOperation,
  deleteForestryOperation,
  listForestTypes,
  createForestType,
  updateForestType,
  deleteForestType,
  listOperationTypes,
  createOperationType,
  updateOperationType,
  deleteOperationType,
  listInterventionTypes,
  createInterventionType,
  updateInterventionType,
  deleteInterventionType,
  importForestryFromExcel,
  updateOperationTypeLinks,
  updateInterventionTypeLinks,
  deriveOperationLinkedIds,
} from '../services/forestryService';
import { parseForestryOperationsFile } from '../utils/excelImport';
import type { ForestryOperation, ForestType, OperationType, InterventionType } from '../types';

// ---------------------------------------------------------------------------
// Form interfaces
// ---------------------------------------------------------------------------

interface OperationFormState {
  description: string;
  minValue: string;
  avgValue: string;
  maxValue: string;
  pricePerHour: string;
  fixedPrice: string;
  unit: string;
  totalUnits: string;
  avgTotalCost: string;
  minValueConditions: string;
  maxValueConditions: string;
  source: string;
  isActive: boolean;
  linkedInterventionTypeIds: string[];
  linkedOperationTypeIds: string[];
  linkedForestTypeIds: string[];
}

const DEFAULT_OPERATION_FORM: OperationFormState = {
  description: '',
  minValue: '',
  avgValue: '',
  maxValue: '',
  pricePerHour: '',
  fixedPrice: '',
  unit: 'ha',
  totalUnits: '',
  avgTotalCost: '',
  minValueConditions: '',
  maxValueConditions: '',
  source: '',
  isActive: true,
  linkedInterventionTypeIds: [],
  linkedOperationTypeIds: [],
  linkedForestTypeIds: [],
};

interface SimpleFormState {
  name: string;
  order: string;
}

const DEFAULT_SIMPLE_FORM: SimpleFormState = { name: '', order: '' };

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function ForestryOperationsPage() {
  const [activeTab, setActiveTab] = useState<
    'operations' | 'forestTypes' | 'operationTypes' | 'interventionTypes'
  >('operations');

  // =========================================================================
  // Tab 1 — Operations
  // =========================================================================

  const [items, setItems] = useState<ForestryOperation[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [lastDoc, setLastDoc] = useState<QueryDocumentSnapshot<DocumentData> | null>(null);
  const [pageHistory, setPageHistory] = useState<(QueryDocumentSnapshot<DocumentData> | null)[]>(
    []
  );

  const [searchQuery, setSearchQuery] = useState('');
  const [forestTypeFilter, setForestTypeFilter] = useState('');
  const [operationTypeFilter, setOperationTypeFilter] = useState('');
  const [isActiveFilter, setIsActiveFilter] = useState('');

  const [forestTypes, setForestTypes] = useState<ForestType[]>([]);
  const [operationTypes, setOperationTypes] = useState<OperationType[]>([]);
  const [allInterventionTypes, setAllInterventionTypes] = useState<
    (InterventionType & { linkedOperationTypeIds?: string[]; linkedForestTypeIds?: string[] })[]
  >([]);

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<ForestryOperation | null>(null);
  const [form, setForm] = useState<OperationFormState>(DEFAULT_OPERATION_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [importing, setImporting] = useState(false);

  // Load reference data on mount
  useEffect(() => {
    listForestTypes()
      .then(setForestTypes)
      .catch(err => console.error('Failed to load forest types:', err));
    listOperationTypes()
      .then(r => setOperationTypes(r as OperationType[]))
      .catch(err => console.error('Failed to load operation types:', err));
    listInterventionTypes()
      .then(data =>
        setAllInterventionTypes(
          data as (InterventionType & {
            linkedOperationTypeIds?: string[];
            linkedForestTypeIds?: string[];
          })[]
        )
      )
      .catch(err => console.error('Failed to load intervention types:', err));
  }, []);

  const loadOperations = useCallback(
    async (cursor: QueryDocumentSnapshot<DocumentData> | null) => {
      setLoading(true);
      try {
        const result = await listForestryOperations({
          pageSize: 50,
          lastDoc: cursor,
          searchQuery,
          forestTypeId: forestTypeFilter,
          operationTypeId: operationTypeFilter,
          isActive: isActiveFilter === '' ? null : isActiveFilter === 'true',
        });
        setItems(result.items);
        setLastDoc(result.lastDoc);
        setHasMore(result.hasMore);
      } catch (err) {
        console.error('Failed to load forestry operations:', err);
        alert('Failed to load forestry operations.');
      } finally {
        setLoading(false);
      }
    },
    [searchQuery, forestTypeFilter, operationTypeFilter, isActiveFilter]
  );

  // Reset and reload when filters change
  useEffect(() => {
    setLastDoc(null);
    setItems([]);
    setHasMore(false);
    setPageHistory([]);
    loadOperations(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, forestTypeFilter, operationTypeFilter, isActiveFilter]);

  function handleNextPage() {
    setPageHistory(h => [...h, lastDoc]);
    loadOperations(lastDoc);
  }

  function handlePreviousPage() {
    const prev = pageHistory[pageHistory.length - 1] ?? null;
    setPageHistory(h => h.slice(0, -1));
    loadOperations(prev);
  }

  const currentPage = pageHistory.length + 1;

  function openAdd() {
    setEditing(null);
    setForm(DEFAULT_OPERATION_FORM);
    setFormError(null);
    setShowModal(true);
  }

  function openEdit(op: ForestryOperation) {
    setEditing(op);
    setForm({
      description: op.description,
      minValue: String(op.minValue),
      avgValue: String(op.avgValue),
      maxValue: String(op.maxValue),
      pricePerHour:
        op.pricePerHour !== null && op.pricePerHour !== undefined ? String(op.pricePerHour) : '',
      fixedPrice:
        op.fixedPrice !== null && op.fixedPrice !== undefined ? String(op.fixedPrice) : '',
      unit: op.unit,
      totalUnits:
        op.totalUnits !== null && op.totalUnits !== undefined ? String(op.totalUnits) : '',
      avgTotalCost:
        op.avgTotalCost !== null && op.avgTotalCost !== undefined ? String(op.avgTotalCost) : '',
      minValueConditions: op.minValueConditions ?? '',
      maxValueConditions: op.maxValueConditions ?? '',
      source: op.source ?? '',
      isActive: op.isActive,
      linkedInterventionTypeIds: op.linkedInterventionTypeIds ?? [],
      linkedOperationTypeIds: op.linkedOperationTypeIds ?? [],
      linkedForestTypeIds: op.linkedForestTypeIds ?? [],
    });
    setFormError(null);
    setShowModal(true);
  }

  function closeModal() {
    setShowModal(false);
    setEditing(null);
    setFormError(null);
  }

  async function handleSave() {
    if (!form.description.trim()) {
      setFormError('Description is required.');
      return;
    }

    setSaving(true);
    setFormError(null);
    try {
      const derived = deriveOperationLinkedIds(
        form.linkedInterventionTypeIds,
        allInterventionTypes,
        operationTypeItems
      );
      const payload: Omit<ForestryOperation, 'id' | 'createdAt' | 'updatedAt'> = {
        description: form.description.trim(),
        minValue: form.minValue !== '' ? parseFloat(form.minValue) : 0,
        avgValue: form.avgValue !== '' ? parseFloat(form.avgValue) : 0,
        maxValue: form.maxValue !== '' ? parseFloat(form.maxValue) : 0,
        pricePerHour: form.pricePerHour !== '' ? parseFloat(form.pricePerHour) : null,
        fixedPrice: form.fixedPrice !== '' ? parseFloat(form.fixedPrice) : null,
        unit: form.unit,
        totalUnits: form.totalUnits !== '' ? parseFloat(form.totalUnits) : null,
        avgTotalCost: form.avgTotalCost !== '' ? parseFloat(form.avgTotalCost) : null,
        minValueConditions: form.minValueConditions,
        maxValueConditions: form.maxValueConditions,
        source: form.source || null,
        isActive: form.isActive,
        linkedInterventionTypeIds: form.linkedInterventionTypeIds,
        linkedOperationTypeIds: derived.linkedOperationTypeIds,
        linkedForestTypeIds: derived.linkedForestTypeIds,
        searchKeywords: [],
      };

      if (editing) {
        await updateForestryOperation(editing.id, payload);
      } else {
        await createForestryOperation(payload);
      }

      closeModal();
      const result = await listForestryOperations({
        pageSize: 50,
        lastDoc: null,
        searchQuery,
        forestTypeId: forestTypeFilter,
        operationTypeId: operationTypeFilter,
        isActive: isActiveFilter === '' ? null : isActiveFilter === 'true',
      });
      setItems(result.items);
      setLastDoc(result.lastDoc);
      setHasMore(result.hasMore);
    } catch (err) {
      console.error('Failed to save operation:', err);
      setFormError('Failed to save operation. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(op: ForestryOperation) {
    if (!confirm(`Delete operation "${op.description}"?`)) return;
    try {
      await deleteForestryOperation(op.id);
      setItems(prev => prev.filter(i => i.id !== op.id));
    } catch (err) {
      console.error('Failed to delete operation:', err);
      alert('Failed to delete operation.');
    }
  }

  async function handleForestryExcelImport(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setImporting(true);
    try {
      const parsed = await parseForestryOperationsFile(file);
      await importForestryFromExcel(parsed);
      const result = await listForestryOperations({
        pageSize: 50,
        lastDoc: null,
        searchQuery,
        forestTypeId: forestTypeFilter,
        operationTypeId: operationTypeFilter,
        isActive: isActiveFilter === '' ? null : isActiveFilter === 'true',
      });
      setItems(result.items);
      setLastDoc(result.lastDoc);
      setHasMore(result.hasMore);
      // Reload reference data too
      listForestTypes()
        .then(setForestTypes)
        .catch(() => {});
      listOperationTypes()
        .then(r => setOperationTypes(r as OperationType[]))
        .catch(() => {});
      alert(`Import successful: ${parsed.operations.length} operations imported.`);
    } catch (err: unknown) {
      console.error('Import failed:', err);
      alert('Import failed: ' + (err instanceof Error ? err.message : String(err)));
    } finally {
      setImporting(false);
    }
  }

  // =========================================================================
  // Tab 2 — Forest Types
  // =========================================================================

  const [forestTypeItems, setForestTypeItems] = useState<ForestType[]>([]);
  const [ftLoading, setFtLoading] = useState(false);
  const [showFtModal, setShowFtModal] = useState(false);
  const [editingFt, setEditingFt] = useState<ForestType | null>(null);
  const [ftForm, setFtForm] = useState<SimpleFormState>(DEFAULT_SIMPLE_FORM);
  const [ftSaving, setFtSaving] = useState(false);
  const [ftError, setFtError] = useState<string | null>(null);

  const forestTypesLoaded = useRef(false);

  async function loadForestTypes() {
    setFtLoading(true);
    try {
      const data = await listForestTypes();
      setForestTypeItems(data);
      forestTypesLoaded.current = true;
    } catch (err) {
      console.error('Failed to load forest types:', err);
      alert('Failed to load forest types.');
    } finally {
      setFtLoading(false);
    }
  }

  useEffect(() => {
    if (activeTab === 'forestTypes' && !forestTypesLoaded.current) {
      loadForestTypes();
    }
  }, [activeTab]);

  function openFtAdd() {
    setEditingFt(null);
    setFtForm(DEFAULT_SIMPLE_FORM);
    setFtError(null);
    setShowFtModal(true);
  }

  function openFtEdit(ft: ForestType) {
    setEditingFt(ft);
    setFtForm({ name: ft.name, order: String(ft.order) });
    setFtError(null);
    setShowFtModal(true);
  }

  function closeFtModal() {
    setShowFtModal(false);
    setEditingFt(null);
    setFtError(null);
  }

  async function handleFtSave() {
    if (!ftForm.name.trim()) {
      setFtError('Name is required.');
      return;
    }
    setFtSaving(true);
    setFtError(null);
    try {
      const payload = {
        name: ftForm.name.trim(),
        order: ftForm.order !== '' ? parseInt(ftForm.order, 10) : 0,
      };
      if (editingFt) {
        await updateForestType(editingFt.id, payload);
      } else {
        await createForestType(payload);
      }
      closeFtModal();
      await loadForestTypes();
    } catch (err) {
      console.error('Failed to save forest type:', err);
      setFtError('Failed to save forest type. Please try again.');
    } finally {
      setFtSaving(false);
    }
  }

  async function handleFtDelete(ft: ForestType) {
    if (!confirm(`Delete forest type "${ft.name}"?`)) return;
    try {
      await deleteForestType(ft.id);
      setForestTypeItems(prev => prev.filter(i => i.id !== ft.id));
    } catch (err) {
      console.error('Failed to delete forest type:', err);
      alert('Failed to delete forest type.');
    }
  }

  // =========================================================================
  // Tab 3 — Operation Types
  // =========================================================================

  const [operationTypeItems, setOperationTypeItems] = useState<
    (OperationType & { linkedForestTypeIds?: string[] })[]
  >([]);
  const [otLoading, setOtLoading] = useState(false);
  const [showOtModal, setShowOtModal] = useState(false);
  const [editingOt, setEditingOt] = useState<OperationType | null>(null);
  const [otForm, setOtForm] = useState<SimpleFormState>(DEFAULT_SIMPLE_FORM);
  const [otLinkedForestTypeIds, setOtLinkedForestTypeIds] = useState<string[]>([]);
  const [otSaving, setOtSaving] = useState(false);
  const [otError, setOtError] = useState<string | null>(null);

  const operationTypesLoaded = useRef(false);

  async function loadOperationTypeItems() {
    setOtLoading(true);
    try {
      const data = await listOperationTypes();
      setOperationTypeItems(data as (OperationType & { linkedForestTypeIds?: string[] })[]);
      operationTypesLoaded.current = true;
    } catch (err) {
      console.error('Failed to load operation types:', err);
      alert('Failed to load operation types.');
    } finally {
      setOtLoading(false);
    }
  }

  useEffect(() => {
    if (activeTab === 'operationTypes' && !operationTypesLoaded.current) {
      loadOperationTypeItems();
    }
  }, [activeTab]);

  function openOtAdd() {
    setEditingOt(null);
    setOtForm(DEFAULT_SIMPLE_FORM);
    setOtLinkedForestTypeIds([]);
    setOtError(null);
    setShowOtModal(true);
  }

  function openOtEdit(ot: OperationType) {
    setEditingOt(ot);
    setOtForm({ name: ot.name, order: String(ot.order) });
    setOtLinkedForestTypeIds(
      (ot as OperationType & { linkedForestTypeIds?: string[] }).linkedForestTypeIds ?? []
    );
    setOtError(null);
    setShowOtModal(true);
  }

  function closeOtModal() {
    setShowOtModal(false);
    setEditingOt(null);
    setOtError(null);
  }

  async function handleOtSave() {
    if (!otForm.name.trim()) {
      setOtError('Name is required.');
      return;
    }
    setOtSaving(true);
    setOtError(null);
    try {
      const base = {
        name: otForm.name.trim(),
        order: otForm.order !== '' ? parseInt(otForm.order, 10) : 0,
      };
      if (editingOt) {
        await updateOperationTypeLinks(editingOt.id, otLinkedForestTypeIds, operationTypeItems);
        await updateOperationType(editingOt.id, base);
      } else {
        await createOperationType({ ...base, linkedForestTypeIds: otLinkedForestTypeIds });
      }
      closeOtModal();
      await loadOperationTypeItems();
    } catch (err) {
      console.error('Failed to save operation type:', err);
      setOtError('Failed to save operation type. Please try again.');
    } finally {
      setOtSaving(false);
    }
  }

  async function handleOtDelete(ot: OperationType) {
    if (!confirm(`Delete operation type "${ot.name}"?`)) return;
    try {
      await deleteOperationType(ot.id);
      setOperationTypeItems(prev => prev.filter(i => i.id !== ot.id));
    } catch (err) {
      console.error('Failed to delete operation type:', err);
      alert('Failed to delete operation type.');
    }
  }

  // =========================================================================
  // Tab 4 — Intervention Types
  // =========================================================================

  const [interventionTypeItems, setInterventionTypeItems] = useState<
    (InterventionType & { linkedForestTypeIds?: string[]; linkedOperationTypeIds?: string[] })[]
  >([]);
  const [itLoading, setItLoading] = useState(false);
  const [showItModal, setShowItModal] = useState(false);
  const [editingIt, setEditingIt] = useState<InterventionType | null>(null);
  const [itForm, setItForm] = useState<SimpleFormState>(DEFAULT_SIMPLE_FORM);
  const [itLinkedOperationTypeIds, setItLinkedOperationTypeIds] = useState<string[]>([]);
  const [itSaving, setItSaving] = useState(false);
  const [itError, setItError] = useState<string | null>(null);

  const interventionTypesLoaded = useRef(false);

  async function loadInterventionTypeItems() {
    setItLoading(true);
    try {
      const data = await listInterventionTypes();
      setInterventionTypeItems(
        data as (InterventionType & {
          linkedForestTypeIds?: string[];
          linkedOperationTypeIds?: string[];
        })[]
      );
      interventionTypesLoaded.current = true;
    } catch (err) {
      console.error('Failed to load intervention types:', err);
      alert('Failed to load intervention types.');
    } finally {
      setItLoading(false);
    }
  }

  useEffect(() => {
    if (activeTab === 'interventionTypes') {
      if (!interventionTypesLoaded.current) {
        loadInterventionTypeItems();
      }
      if (!operationTypesLoaded.current) {
        loadOperationTypeItems();
      }
    }
  }, [activeTab]);

  function openItAdd() {
    setEditingIt(null);
    setItForm(DEFAULT_SIMPLE_FORM);
    setItLinkedOperationTypeIds([]);
    setItError(null);
    setShowItModal(true);
  }

  function openItEdit(it: InterventionType) {
    setEditingIt(it);
    setItForm({ name: it.name, order: String(it.order) });
    setItLinkedOperationTypeIds(
      (it as InterventionType & { linkedOperationTypeIds?: string[] }).linkedOperationTypeIds ?? []
    );
    setItError(null);
    setShowItModal(true);
  }

  function closeItModal() {
    setShowItModal(false);
    setEditingIt(null);
    setItError(null);
  }

  async function handleItSave() {
    if (!itForm.name.trim()) {
      setItError('Name is required.');
      return;
    }
    setItSaving(true);
    setItError(null);
    try {
      const base = {
        name: itForm.name.trim(),
        order: itForm.order !== '' ? parseInt(itForm.order, 10) : 0,
      };
      if (editingIt) {
        await updateInterventionTypeLinks(
          editingIt.id,
          itLinkedOperationTypeIds,
          operationTypeItems
        );
        await updateInterventionType(editingIt.id, base);
      } else {
        const linkedForestTypeIds = [
          ...new Set(
            itLinkedOperationTypeIds.flatMap(
              otId => operationTypeItems.find(ot => ot.id === otId)?.linkedForestTypeIds ?? []
            )
          ),
        ];
        await createInterventionType({
          ...base,
          linkedOperationTypeIds: itLinkedOperationTypeIds,
          linkedForestTypeIds,
        });
      }
      closeItModal();
      await loadInterventionTypeItems();
    } catch (err) {
      console.error('Failed to save intervention type:', err);
      setItError('Failed to save intervention type. Please try again.');
    } finally {
      setItSaving(false);
    }
  }

  async function handleItDelete(it: InterventionType) {
    if (!confirm(`Delete intervention type "${it.name}"?`)) return;
    try {
      await deleteInterventionType(it.id);
      setInterventionTypeItems(prev => prev.filter(i => i.id !== it.id));
    } catch (err) {
      console.error('Failed to delete intervention type:', err);
      alert('Failed to delete intervention type.');
    }
  }

  // =========================================================================
  // Render
  // =========================================================================

  return (
    <div className="page-header">
      <div className="row g-2 align-items-center">
        <div className="col">
          <div className="page-pretitle">Market</div>
          <h2 className="page-title">Forestry Operations</h2>
        </div>
        {activeTab === 'operations' && (
          <div className="col-auto d-flex gap-2">
            {importing ? (
              <button className="btn btn-outline-secondary" disabled>
                <span className="spinner-border spinner-border-sm me-1" />
                Importing…
              </button>
            ) : (
              <label className="btn btn-outline-secondary" style={{ cursor: 'pointer' }}>
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  style={{ display: 'none' }}
                  onChange={handleForestryExcelImport}
                />
                <IconUpload className="icon me-1" />
                Import Excel
              </label>
            )}
            <button className="btn btn-primary" onClick={openAdd}>
              <IconPlus className="icon" />
              Add Operation
            </button>
          </div>
        )}
        {activeTab === 'forestTypes' && (
          <div className="col-auto">
            <button className="btn btn-primary" onClick={openFtAdd}>
              <IconPlus className="icon" />
              Add Forest Type
            </button>
          </div>
        )}
        {activeTab === 'operationTypes' && (
          <div className="col-auto">
            <button className="btn btn-primary" onClick={openOtAdd}>
              <IconPlus className="icon" />
              Add Operation Type
            </button>
          </div>
        )}
        {activeTab === 'interventionTypes' && (
          <div className="col-auto">
            <button className="btn btn-primary" onClick={openItAdd}>
              <IconPlus className="icon" />
              Add Intervention Type
            </button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <ul className="nav nav-tabs mb-3 mt-3">
        <li className="nav-item">
          <button
            className={`nav-link${activeTab === 'operations' ? ' active' : ''}`}
            onClick={() => setActiveTab('operations')}
          >
            Operations
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link${activeTab === 'forestTypes' ? ' active' : ''}`}
            onClick={() => setActiveTab('forestTypes')}
          >
            Forest Types
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link${activeTab === 'operationTypes' ? ' active' : ''}`}
            onClick={() => setActiveTab('operationTypes')}
          >
            Operation Types
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link${activeTab === 'interventionTypes' ? ' active' : ''}`}
            onClick={() => setActiveTab('interventionTypes')}
          >
            Intervention Types
          </button>
        </li>
      </ul>

      {/* ===================================================================
          TAB 1 — Operations
      ==================================================================== */}
      {activeTab === 'operations' && (
        <>
          {/* Filters */}
          <div className="card mt-3">
            <div className="card-body">
              <div className="row g-2">
                <div className="col-12 col-md-4">
                  <label className="form-label">Search</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search by description…"
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                  />
                </div>
                <div className="col-12 col-md-3">
                  <label className="form-label">Forest Type</label>
                  <select
                    className="form-select"
                    value={forestTypeFilter}
                    onChange={e => setForestTypeFilter(e.target.value)}
                  >
                    <option value="">All Forest Types</option>
                    {forestTypes.map(ft => (
                      <option key={ft.id} value={ft.id}>
                        {ft.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-12 col-md-3">
                  <label className="form-label">Operation Type</label>
                  <select
                    className="form-select"
                    value={operationTypeFilter}
                    onChange={e => setOperationTypeFilter(e.target.value)}
                  >
                    <option value="">All Operation Types</option>
                    {operationTypes.map(ot => (
                      <option key={ot.id} value={ot.id}>
                        {ot.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-12 col-md-2">
                  <label className="form-label">Status</label>
                  <select
                    className="form-select"
                    value={isActiveFilter}
                    onChange={e => setIsActiveFilter(e.target.value)}
                  >
                    <option value="">All</option>
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="card mt-3">
            <div className="table-responsive">
              <table className="table table-vcenter card-table">
                <thead>
                  <tr>
                    <th>Description</th>
                    <th>Forest Types</th>
                    <th>Operation Types</th>
                    <th>Min Value</th>
                    <th>Avg Value</th>
                    <th>Max Value</th>
                    <th>Unit</th>
                    <th>Active</th>
                    <th className="w-1">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && items.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="text-center py-4">
                        <span className="spinner-border spinner-border-sm me-2" />
                        Loading…
                      </td>
                    </tr>
                  ) : items.length === 0 ? (
                    <tr>
                      <td colSpan={9}>
                        <div className="empty">
                          <p className="empty-title">No operations found</p>
                          <p className="empty-subtitle text-muted">
                            Try adjusting your filters or import an Excel file
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    items.map(item => (
                      <tr key={item.id}>
                        <td>{item.description}</td>
                        <td>
                          <span className="badge bg-blue-lt">
                            {(item.linkedForestTypeIds ?? []).length}
                          </span>
                        </td>
                        <td>
                          <span className="badge bg-azure-lt">
                            {(item.linkedOperationTypeIds ?? []).length}
                          </span>
                        </td>
                        <td>{item.minValue.toFixed(2)}</td>
                        <td>{item.avgValue.toFixed(2)}</td>
                        <td>{item.maxValue.toFixed(2)}</td>
                        <td>{item.unit}</td>
                        <td>
                          {item.isActive ? (
                            <span className="badge bg-success text-white">Active</span>
                          ) : (
                            <span className="badge bg-secondary text-white">Inactive</span>
                          )}
                        </td>
                        <td>
                          <div className="btn-list flex-nowrap">
                            <button
                              className="btn btn-icon"
                              title="Edit"
                              onClick={() => openEdit(item)}
                            >
                              <IconPencil className="icon" />
                            </button>
                            <button
                              className="btn btn-icon text-danger"
                              title="Delete"
                              onClick={() => handleDelete(item)}
                            >
                              <IconTrash className="icon" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div className="card-footer d-flex align-items-center">
              <p className="m-0 text-muted">Page {currentPage}</p>
              <ul className="pagination m-0 ms-auto">
                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                  <button
                    className="page-link"
                    onClick={handlePreviousPage}
                    disabled={currentPage === 1}
                  >
                    <IconChevronLeft size={16} />
                    prev
                  </button>
                </li>
                <li className={`page-item ${!hasMore ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={handleNextPage} disabled={!hasMore}>
                    next
                    <IconChevronRight size={16} />
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </>
      )}

      {/* ===================================================================
          TAB 2 — Forest Types
      ==================================================================== */}
      {activeTab === 'forestTypes' && (
        <>
          <div className="card mt-3">
            <div className="table-responsive">
              <table className="table table-vcenter card-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Order</th>
                    <th className="w-1">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {ftLoading && forestTypeItems.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="text-center py-4">
                        <span className="spinner-border spinner-border-sm me-2" />
                        Loading…
                      </td>
                    </tr>
                  ) : forestTypeItems.length === 0 ? (
                    <tr>
                      <td colSpan={3}>
                        <div className="empty">
                          <p className="empty-title">No forest types found</p>
                          <p className="empty-subtitle text-muted">
                            Add a forest type to get started
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    forestTypeItems.map(ft => (
                      <tr key={ft.id}>
                        <td className="fw-bold">{ft.name}</td>
                        <td>{ft.order}</td>
                        <td>
                          <div className="btn-list flex-nowrap">
                            <button
                              className="btn btn-icon"
                              title="Edit"
                              onClick={() => openFtEdit(ft)}
                            >
                              <IconPencil className="icon" />
                            </button>
                            <button
                              className="btn btn-icon text-danger"
                              title="Delete"
                              onClick={() => handleFtDelete(ft)}
                            >
                              <IconTrash className="icon" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* ===================================================================
          TAB 3 — Operation Types
      ==================================================================== */}
      {activeTab === 'operationTypes' && (
        <>
          <div className="card mt-3">
            <div className="table-responsive">
              <table className="table table-vcenter card-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Order</th>
                    <th>Linked Forest Types</th>
                    <th className="w-1">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {otLoading && operationTypeItems.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="text-center py-4">
                        <span className="spinner-border spinner-border-sm me-2" />
                        Loading…
                      </td>
                    </tr>
                  ) : operationTypeItems.length === 0 ? (
                    <tr>
                      <td colSpan={4}>
                        <div className="empty">
                          <p className="empty-title">No operation types found</p>
                          <p className="empty-subtitle text-muted">
                            Add an operation type to get started
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    operationTypeItems.map(ot => (
                      <tr key={ot.id}>
                        <td className="fw-bold">{ot.name}</td>
                        <td>{ot.order}</td>
                        <td>
                          {(ot.linkedForestTypeIds ?? []).map(ftId => {
                            const ft = forestTypeItems.find(f => f.id === ftId);
                            return ft ? (
                              <span key={ftId} className="badge bg-blue-lt me-1">
                                {ft.name}
                              </span>
                            ) : null;
                          })}
                          {(ot.linkedForestTypeIds ?? []).length === 0 && (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                        <td>
                          <div className="btn-list flex-nowrap">
                            <button
                              className="btn btn-icon"
                              title="Edit"
                              onClick={() => openOtEdit(ot)}
                            >
                              <IconPencil className="icon" />
                            </button>
                            <button
                              className="btn btn-icon text-danger"
                              title="Delete"
                              onClick={() => handleOtDelete(ot)}
                            >
                              <IconTrash className="icon" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* ===================================================================
          TAB 4 — Intervention Types
      ==================================================================== */}
      {activeTab === 'interventionTypes' && (
        <>
          <div className="card mt-3">
            <div className="table-responsive">
              <table className="table table-vcenter card-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Order</th>
                    <th>Linked Operation Types</th>
                    <th className="w-1">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {itLoading && interventionTypeItems.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="text-center py-4">
                        <span className="spinner-border spinner-border-sm me-2" />
                        Loading…
                      </td>
                    </tr>
                  ) : interventionTypeItems.length === 0 ? (
                    <tr>
                      <td colSpan={4}>
                        <div className="empty">
                          <p className="empty-title">No intervention types found</p>
                          <p className="empty-subtitle text-muted">
                            Add an intervention type to get started
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    interventionTypeItems.map(it => (
                      <tr key={it.id}>
                        <td className="fw-bold">{it.name}</td>
                        <td>{it.order}</td>
                        <td>
                          {(it.linkedOperationTypeIds ?? []).map(otId => {
                            const ot = operationTypeItems.find(o => o.id === otId);
                            return ot ? (
                              <span key={otId} className="badge bg-azure-lt me-1">
                                {ot.name}
                              </span>
                            ) : null;
                          })}
                          {(it.linkedOperationTypeIds ?? []).length === 0 && (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                        <td>
                          <div className="btn-list flex-nowrap">
                            <button
                              className="btn btn-icon"
                              title="Edit"
                              onClick={() => openItEdit(it)}
                            >
                              <IconPencil className="icon" />
                            </button>
                            <button
                              className="btn btn-icon text-danger"
                              title="Delete"
                              onClick={() => handleItDelete(it)}
                            >
                              <IconTrash className="icon" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* ===================================================================
          MODAL — Create / Edit Operation
      ==================================================================== */}
      {showModal && (
        <div
          className="modal modal-blur fade show"
          tabIndex={-1}
          role="dialog"
          style={{ display: 'block' }}
        >
          <div
            className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable"
            role="document"
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editing ? 'Edit Operation' : 'Add Operation'}</h5>
                <button type="button" className="btn-close" onClick={closeModal} />
              </div>
              <div className="modal-body">
                {formError && <div className="alert alert-danger mb-3">{formError}</div>}
                <div className="row g-3">
                  {/* Description */}
                  <div className="col-12">
                    <label className="form-label required">Description</label>
                    <textarea
                      className="form-control"
                      rows={2}
                      value={form.description}
                      onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Operation description"
                    />
                  </div>

                  {/* Unit */}
                  <div className="col-md-4">
                    <label className="form-label">Unit</label>
                    <input
                      type="text"
                      className="form-control"
                      value={form.unit}
                      onChange={e => setForm(prev => ({ ...prev, unit: e.target.value }))}
                      placeholder="ha"
                    />
                  </div>

                  {/* Min / Avg / Max Value */}
                  <div className="col-md-4">
                    <label className="form-label">Min Value</label>
                    <input
                      type="number"
                      className="form-control"
                      value={form.minValue}
                      onChange={e => setForm(prev => ({ ...prev, minValue: e.target.value }))}
                      placeholder="0.00"
                      step="0.01"
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Avg Value</label>
                    <input
                      type="number"
                      className="form-control"
                      value={form.avgValue}
                      onChange={e => setForm(prev => ({ ...prev, avgValue: e.target.value }))}
                      placeholder="0.00"
                      step="0.01"
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Max Value</label>
                    <input
                      type="number"
                      className="form-control"
                      value={form.maxValue}
                      onChange={e => setForm(prev => ({ ...prev, maxValue: e.target.value }))}
                      placeholder="0.00"
                      step="0.01"
                    />
                  </div>

                  {/* Price per Hour */}
                  <div className="col-md-4">
                    <label className="form-label">Price per Hour</label>
                    <input
                      type="number"
                      className="form-control"
                      value={form.pricePerHour}
                      onChange={e => setForm(prev => ({ ...prev, pricePerHour: e.target.value }))}
                      placeholder="Optional"
                      step="0.01"
                    />
                  </div>

                  {/* Fixed Price */}
                  <div className="col-md-4">
                    <label className="form-label">Fixed Price</label>
                    <input
                      type="number"
                      className="form-control"
                      value={form.fixedPrice}
                      onChange={e => setForm(prev => ({ ...prev, fixedPrice: e.target.value }))}
                      placeholder="Optional"
                      step="0.01"
                    />
                  </div>

                  {/* Total Units */}
                  <div className="col-md-4">
                    <label className="form-label">Total Units</label>
                    <input
                      type="number"
                      className="form-control"
                      value={form.totalUnits}
                      onChange={e => setForm(prev => ({ ...prev, totalUnits: e.target.value }))}
                      placeholder="Optional"
                      step="0.01"
                    />
                  </div>

                  {/* Avg Total Cost */}
                  <div className="col-md-4">
                    <label className="form-label">Avg Total Cost</label>
                    <input
                      type="number"
                      className="form-control"
                      value={form.avgTotalCost}
                      onChange={e => setForm(prev => ({ ...prev, avgTotalCost: e.target.value }))}
                      placeholder="Optional"
                      step="0.01"
                    />
                  </div>

                  {/* Min Value Conditions */}
                  <div className="col-12">
                    <label className="form-label">Min Value Conditions</label>
                    <textarea
                      className="form-control"
                      rows={2}
                      value={form.minValueConditions}
                      onChange={e =>
                        setForm(prev => ({ ...prev, minValueConditions: e.target.value }))
                      }
                      placeholder="Optional"
                    />
                  </div>

                  {/* Max Value Conditions */}
                  <div className="col-12">
                    <label className="form-label">Max Value Conditions</label>
                    <textarea
                      className="form-control"
                      rows={2}
                      value={form.maxValueConditions}
                      onChange={e =>
                        setForm(prev => ({ ...prev, maxValueConditions: e.target.value }))
                      }
                      placeholder="Optional"
                    />
                  </div>

                  {/* Source */}
                  <div className="col-12">
                    <label className="form-label">Source</label>
                    <input
                      type="text"
                      className="form-control"
                      value={form.source}
                      onChange={e => setForm(prev => ({ ...prev, source: e.target.value }))}
                      placeholder="Optional"
                    />
                  </div>

                  {/* Is Active */}
                  <div className="col-12">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="opIsActive"
                        checked={form.isActive}
                        onChange={e => setForm(prev => ({ ...prev, isActive: e.target.checked }))}
                      />
                      <label className="form-check-label" htmlFor="opIsActive">
                        Active
                      </label>
                    </div>
                  </div>

                  {/* Linked Intervention Types */}
                  <div className="col-12">
                    <label className="form-label">Linked Intervention Types</label>
                    {allInterventionTypes.length === 0 ? (
                      <p className="text-muted small">No intervention types available.</p>
                    ) : (
                      <div className="row g-1">
                        {allInterventionTypes.map(it => (
                          <div className="col-6" key={it.id}>
                            <label className="form-check">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                checked={form.linkedInterventionTypeIds.includes(it.id)}
                                onChange={e => {
                                  const newIds = e.target.checked
                                    ? [...form.linkedInterventionTypeIds, it.id]
                                    : form.linkedInterventionTypeIds.filter(id => id !== it.id);
                                  setForm(prev => ({ ...prev, linkedInterventionTypeIds: newIds }));
                                }}
                              />
                              <span className="form-check-label">{it.name}</span>
                            </label>
                          </div>
                        ))}
                      </div>
                    )}
                    {form.linkedInterventionTypeIds.length > 0 && (
                      <div className="mt-2 small text-muted">
                        Linked operation types and forest types will be derived automatically.
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-link link-secondary me-auto"
                  onClick={closeModal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Saving…
                    </>
                  ) : editing ? (
                    'Save Changes'
                  ) : (
                    'Add Operation'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===================================================================
          MODAL — Create / Edit Forest Type
      ==================================================================== */}
      {showFtModal && (
        <div
          className="modal modal-blur fade show"
          tabIndex={-1}
          role="dialog"
          style={{ display: 'block' }}
        >
          <div
            className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable"
            role="document"
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editingFt ? 'Edit Forest Type' : 'Add Forest Type'}
                </h5>
                <button type="button" className="btn-close" onClick={closeFtModal} />
              </div>
              <div className="modal-body">
                {ftError && <div className="alert alert-danger mb-3">{ftError}</div>}
                <div className="row g-3">
                  <div className="col-12">
                    <label className="form-label required">Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={ftForm.name}
                      onChange={e => setFtForm(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Forest type name"
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label required">Order</label>
                    <input
                      type="number"
                      className="form-control"
                      value={ftForm.order}
                      onChange={e => setFtForm(prev => ({ ...prev, order: e.target.value }))}
                      placeholder="0"
                      min={0}
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-link link-secondary me-auto"
                  onClick={closeFtModal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleFtSave}
                  disabled={ftSaving}
                >
                  {ftSaving ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Saving…
                    </>
                  ) : editingFt ? (
                    'Save Changes'
                  ) : (
                    'Add Forest Type'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===================================================================
          MODAL — Create / Edit Operation Type
      ==================================================================== */}
      {showOtModal && (
        <div
          className="modal modal-blur fade show"
          tabIndex={-1}
          role="dialog"
          style={{ display: 'block' }}
        >
          <div
            className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable"
            role="document"
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editingOt ? 'Edit Operation Type' : 'Add Operation Type'}
                </h5>
                <button type="button" className="btn-close" onClick={closeOtModal} />
              </div>
              <div className="modal-body">
                {otError && <div className="alert alert-danger mb-3">{otError}</div>}
                <div className="row g-3">
                  <div className="col-12">
                    <label className="form-label required">Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={otForm.name}
                      onChange={e => setOtForm(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Operation type name"
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label required">Order</label>
                    <input
                      type="number"
                      className="form-control"
                      value={otForm.order}
                      onChange={e => setOtForm(prev => ({ ...prev, order: e.target.value }))}
                      placeholder="0"
                      min={0}
                    />
                  </div>
                  {/* Linked Forest Types */}
                  <div className="col-12">
                    <label className="form-label">Linked Forest Types</label>
                    {forestTypeItems.length === 0 ? (
                      <p className="text-muted small">
                        No forest types available. Add forest types first.
                      </p>
                    ) : (
                      <div className="row g-1">
                        {forestTypeItems.map(ft => (
                          <div className="col-6" key={ft.id}>
                            <label className="form-check">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                checked={otLinkedForestTypeIds.includes(ft.id)}
                                onChange={e => {
                                  if (e.target.checked) {
                                    setOtLinkedForestTypeIds(prev => [...prev, ft.id]);
                                  } else {
                                    setOtLinkedForestTypeIds(prev =>
                                      prev.filter(id => id !== ft.id)
                                    );
                                  }
                                }}
                              />
                              <span className="form-check-label">{ft.name}</span>
                            </label>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-link link-secondary me-auto"
                  onClick={closeOtModal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleOtSave}
                  disabled={otSaving}
                >
                  {otSaving ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Saving…
                    </>
                  ) : editingOt ? (
                    'Save Changes'
                  ) : (
                    'Add Operation Type'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===================================================================
          MODAL — Create / Edit Intervention Type
      ==================================================================== */}
      {showItModal && (
        <div
          className="modal modal-blur fade show"
          tabIndex={-1}
          role="dialog"
          style={{ display: 'block' }}
        >
          <div
            className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable"
            role="document"
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editingIt ? 'Edit Intervention Type' : 'Add Intervention Type'}
                </h5>
                <button type="button" className="btn-close" onClick={closeItModal} />
              </div>
              <div className="modal-body">
                {itError && <div className="alert alert-danger mb-3">{itError}</div>}
                <div className="row g-3">
                  <div className="col-12">
                    <label className="form-label required">Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={itForm.name}
                      onChange={e => setItForm(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Intervention type name"
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label required">Order</label>
                    <input
                      type="number"
                      className="form-control"
                      value={itForm.order}
                      onChange={e => setItForm(prev => ({ ...prev, order: e.target.value }))}
                      placeholder="0"
                      min={0}
                    />
                  </div>
                  {/* Linked Operation Types */}
                  <div className="col-12">
                    <label className="form-label">Linked Operation Types</label>
                    {operationTypeItems.length === 0 ? (
                      <p className="text-muted small">
                        No operation types available. Add operation types first.
                      </p>
                    ) : (
                      <div className="row g-1">
                        {operationTypeItems.map(ot => (
                          <div className="col-6" key={ot.id}>
                            <label className="form-check">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                checked={itLinkedOperationTypeIds.includes(ot.id)}
                                onChange={e => {
                                  if (e.target.checked) {
                                    setItLinkedOperationTypeIds(prev => [...prev, ot.id]);
                                  } else {
                                    setItLinkedOperationTypeIds(prev =>
                                      prev.filter(id => id !== ot.id)
                                    );
                                  }
                                }}
                              />
                              <span className="form-check-label">{ot.name}</span>
                            </label>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-link link-secondary me-auto"
                  onClick={closeItModal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleItSave}
                  disabled={itSaving}
                >
                  {itSaving ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Saving…
                    </>
                  ) : editingIt ? (
                    'Save Changes'
                  ) : (
                    'Add Intervention Type'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
